import type { LimitCheckResult, ProjectFileNode } from "./types";

/** Maximum bytes per individual file (~256 KB) */
export const MAX_FILE_BYTES = 256 * 1024;

/** Maximum decoded bytes for indicator icon assets (~32 KB) */
export const MAX_ICON_BYTES = 32 * 1024;

/** Maximum total bytes across all files in a project (~5 MB) */
export const MAX_PROJECT_BYTES = 5 * 1024 * 1024;

/** Maximum number of file rows (folders + files) per project */
export const MAX_FILES = 200;

export function byteSize(content: string): number {
  return new TextEncoder().encode(content).length;
}

export interface LimitCheckInput {
  /** New content for the file being written */
  content: string;
  /** All existing file nodes in the project */
  existingFiles: ProjectFileNode[];
  /** Path of the file being written (to exclude from current size when updating) */
  targetPath?: string;
  /** Whether this is a new node (adds to file count) */
  isNewNode?: boolean;
}

export function checkLimits(input: LimitCheckInput): LimitCheckResult {
  const { content, existingFiles, targetPath, isNewNode } = input;
  const newFileBytes = byteSize(content);

  if (newFileBytes > MAX_FILE_BYTES) {
    return {
      ok: false,
      error: `File exceeds ${formatBytes(MAX_FILE_BYTES)} limit (${formatBytes(newFileBytes)})`,
    };
  }

  const fileNodes = existingFiles.filter((f) => f.type === "file");
  const folderNodes = existingFiles.filter((f) => f.type === "folder");
  const totalNodes = fileNodes.length + folderNodes.length;

  if (isNewNode && totalNodes >= MAX_FILES) {
    return {
      ok: false,
      error: `Project exceeds ${MAX_FILES} node limit`,
    };
  }

  let projectBytes = 0;
  for (const node of existingFiles) {
    if (node.type !== "file") continue;
    if (targetPath && node.path === targetPath) continue;
    projectBytes += node.sizeBytes;
  }
  projectBytes += newFileBytes;

  if (projectBytes > MAX_PROJECT_BYTES) {
    return {
      ok: false,
      error: `Project exceeds ${formatBytes(MAX_PROJECT_BYTES)} limit (${formatBytes(projectBytes)})`,
    };
  }

  return { ok: true };
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${bytes} B`;
}
